// Which code spans in the agent's prose are places in the workbook, and therefore worth a click.
//
// The pane contract asks the agent to write every reference as code (`Sheet!A1`, `output-SPV!J813:J820`),
// so the candidates are already marked up. What is left is deciding which of them really is an address,
// and that needs care in this domain: `P50` and `P90` are the production assumptions of every wind and
// solar model in the book, and both parse as perfectly good A1 cells (column P, rows 50 and 90). Linking
// them would send the user to a cell nobody mentioned.
//
// So a span is only a place when it NAMES A SHEET the workbook actually has. That rules out the whole
// class at a stroke — no bare `P50`, no `IF`, no `SUM` — and it has the second virtue that a click can
// never land on a sheet that is not there. Bare ranges stay plain text; the prompt asks for the sheet.
//
// Once the sheet is known, the address is read leniently. The agent writes places the way people talk
// about them — `C-Operation!row 694`, `C-Operation!b100-b101`, or just `C-Operation` — and each of those
// names somewhere definite, so it is normalised to A1 (`694:694`, `B100:B101`) or, for a sheet alone, to
// an empty address that simply activates the sheet.
//
// Two more shapes are read, because the agent writes them however the prompt is worded:
//  · a list in one span, `input!D180:K185 et D187:K198`: each item is its own place, and an item with no
//    sheet takes the sheet of the place before it (`cellPieces`);
//  · a reference outside code, `(input!D180:K185 et D187:K198)` in plain prose: `findReferences` finds it
//    so the markdown layer can mark it up as code. A sheet is still required there, so prose `P50` stays prose.
import { isValidAddress, splitSheetPrefix } from './a1';

export interface CellTarget {
  sheet: string;
  /** A1, or '' when the span names the sheet alone. */
  address: string;
}

/** A defined name is a word, not an address, and Excel resolves it itself — out of scope here. */
const MAX_LEN = 120;

/** Case- and quote-insensitive lookup of a sheet the workbook has, returning its real spelling. */
function knownSheet(name: string, sheets: readonly string[]): string | null {
  const wanted = name.trim().toLowerCase();
  if (!wanted) return null;
  return sheets.find((s) => s.toLowerCase() === wanted) ?? null;
}

const DASH = String.raw`\s*(?:-|–|—|to|\.\.)\s*`;
const ROWS_RE = new RegExp(String.raw`^rows?\s*(\d{1,7})(?:(?:${DASH}|\s*:\s*)(\d{1,7}))?$`, 'i');
const COLS_RE = new RegExp(String.raw`^(?:columns?|cols?)\s+([a-z]{1,3})(?:(?:${DASH}|\s*:\s*)([a-z]{1,3}))?$`, 'i');
const SPAN_RE = new RegExp(String.raw`^(\$?[a-z]{1,3}\$?\d{1,7}|\d{1,7})${DASH}(\$?[a-z]{1,3}\$?\d{1,7}|\d{1,7})$`, 'i');

/** The A1 form of an address written the way people say it, or null when it is none. */
function normaliseAddress(address: string): string | null {
  const a = address.trim();
  if (isValidAddress(a.replace(/\$/g, ''))) return a.toUpperCase();
  let m = ROWS_RE.exec(a);
  if (m) return `${m[1]}:${m[2] ?? m[1]}`;
  m = COLS_RE.exec(a);
  if (m) return `${m[1]}:${m[2] ?? m[1]}`.toUpperCase();
  m = SPAN_RE.exec(a);
  if (m) {
    const joined = `${m[1]}:${m[2]}`.toUpperCase();
    // Both ends must be the same kind: `B100-B101` and `694-700`, never `B100-700`.
    if (/^\d+$/.test(m[1]) === /^\d+$/.test(m[2]) && isValidAddress(joined.replace(/\$/g, ''))) return joined;
  }
  return null;
}

/**
 * The place a code span points at, or null when it is just code.
 *
 * `sheets` is the workbook's sheet names; without them nothing is a place, which is the right default
 * while the index is still loading — a link that cannot work should not be offered.
 */
export function cellTarget(text: string, sheets: readonly string[]): CellTarget | null {
  const raw = String(text ?? '').trim();
  if (!raw || raw.length > MAX_LEN || raw.includes('\n')) return null;
  // A formula is not a destination, and it is the other thing the agent writes in code.
  if (raw.startsWith('=')) return null;
  const { sheet, address } = splitSheetPrefix(raw);
  if (!sheet) {
    // A sheet named on its own is a place too: the click opens the sheet.
    const unquoted = raw.startsWith("'") && raw.endsWith("'") ? raw.slice(1, -1).replace(/''/g, "'") : raw;
    const alone = knownSheet(unquoted, sheets);
    return alone ? { sheet: alone, address: '' } : null;
  }
  if (!address) return null;
  const real = knownSheet(sheet, sheets);
  if (!real) return null;
  // `$A$1` is as valid a reference as `A1`, and the agent copies them out of formulas.
  const a1 = normaliseAddress(address);
  return a1 ? { sheet: real, address: a1 } : null;
}

/** One piece of a code span: a place, or the text around places (separators, words), shown as it is. */
export interface CellPiece {
  text: string;
  target: CellTarget | null;
}

/** What separates the items of a list of places. A sheet name can hold a comma or "et" but only when it is
 *  quoted, so quoted names are matched first and never split. `+` and `-` are left out: they are arithmetic
 *  and ranges. */
const LIST_SEP = String.raw`\s*[,;&/]\s*|\s+(?:et|and|ou|or|puis|then)\s+`;
const QUOTED_OR_SEP = new RegExp(String.raw`'(?:[^']|'')*'|(${LIST_SEP})`, 'gi');

/**
 * The places in a code span, in order, with the text between them; null when there is none.
 *
 * A span that is one place is one piece. Otherwise the span is read as a list: an item with a sheet is read
 * by `cellTarget`, an item without one takes the sheet of the last place before it, and an item that is no
 * place stays text. An item before any sheet stays text, so `P50, P90` is still no link.
 */
export function cellPieces(text: string, sheets: readonly string[]): CellPiece[] | null {
  const raw = String(text ?? '');
  const whole = cellTarget(raw, sheets);
  if (whole) return [{ text: raw, target: whole }];
  if (!raw.trim() || raw.length > MAX_LEN || raw.includes('\n') || raw.trim().startsWith('=')) return null;

  const pieces: CellPiece[] = [];
  let sheet: string | null = null;
  const item = (part: string) => {
    if (!part) return;
    let target = cellTarget(part, sheets);
    if (!target && sheet && splitSheetPrefix(part).sheet === null) {
      const address = normaliseAddress(part);
      if (address) target = { sheet, address };
    }
    if (target?.address) sheet = target.sheet;
    pieces.push({ text: part, target });
  };
  let last = 0;
  for (const m of raw.matchAll(QUOTED_OR_SEP)) {
    if (m[1] === undefined) continue;
    item(raw.slice(last, m.index));
    pieces.push({ text: m[1], target: null });
    last = m.index + m[0].length;
  }
  item(raw.slice(last));
  return pieces.some((p) => p.target) ? pieces : null;
}

const ADDR = String.raw`\$?[a-z]{1,3}\$?\d{1,7}(?::\$?[a-z]{1,3}\$?\d{1,7})?|\$?\d{1,7}:\$?\d{1,7}|\$?[a-z]{1,3}:\$?[a-z]{1,3}`;
const QUALIFIED = String.raw`(?:'(?:[^'\n]|'')+'|[\p{L}\p{N}_.\-]+)!(?:${ADDR})`;
/** A sheet-qualified A1 reference in prose, followed by any list of places it heads. The look-behind keeps
 *  a match from starting inside a word, or after the `]` of another workbook (`[Prices.xlsx]input!B5` is not
 *  this workbook's `input`); `'` is allowed before it for French elision (`l'input!B5`). */
const PROSE_REF = new RegExp(
  String.raw`(?<![\p{L}\p{N}_.\-!$\]])${QUALIFIED}(?:(?:${LIST_SEP})(?:${QUALIFIED}|${ADDR}))*(?![\p{L}\p{N}_$!(])`,
  'giu',
);

/**
 * Where plain prose names a place of the workbook, as `[start, end)` offsets: what the agent should have put
 * in code and did not. A match counts only when `cellPieces` finds a place in it, so a sheet the workbook
 * does not have is no match.
 */
export function findReferences(text: string, sheets: readonly string[]): Array<{ start: number; end: number }> {
  if (!sheets.length || !text.includes('!')) return [];
  const found: Array<{ start: number; end: number }> = [];
  for (const m of text.matchAll(PROSE_REF)) {
    if (cellPieces(m[0], sheets)) found.push({ start: m.index, end: m.index + m[0].length });
  }
  return found;
}
